import Loading from "../../common/Loading";
import React, {useContext, useEffect, useCallback, useState, useRef} from "react";
import {apiContext} from "../../ApiContext";
import {FullStory} from "@fullstory/browser";
import * as PropTypes from "prop-types";
import {endSpan, setSpanStatus, startSpan} from "../../common/telemetry/OpenTelemetryHelper";
import SpanStatusCode from "../../common/telemetry/SpanStatusCode";
import {getDecodedValue, redirectTo} from "../../common/utils";
import {safeTrack} from "../../common/telemetry/SegmentHelper";
import ErrorViewV2 from "../../common/ErrorViewV2";

const POLLING_TIMEOUT = 60000;
const POLLING_INTERVAL = 2000;
const MESSAGES = [
  'Please wait while we finalise your connection',
  'Almost there, please wait a bit longer',
  "It's taking a little longer than usual, but we're still working on it."
];

function OpenBankingLoadingView({productRedirectUrl, state}) {
  const loadInOpenBankingLoadingViewSpan = startSpan('load_in_open_banking_loading_view_span');
  const api = useContext(apiContext);
  const applicationId = getDecodedValue("applicationId", state);
  const consumerId = getDecodedValue("consumerId", state);
  const [isError, setError] = useState(false);
  const [loadingMessage, setLoadingMessage] = useState(MESSAGES[0]);
  const isPollingRef = useRef(false);
  const pollTimerRef = useRef(null);
  const messageTimerRef = useRef(null);

  useEffect(() => {
    FullStory('trackEvent', {
      name: 'Open Banking Loading page before finalise',
      properties: {
      },
    })
  }, []);

  const pollBfosForConsentId = useCallback(async () => {
    if (!isPollingRef.current) return;

    const bankName = getDecodedValue("institutionName", state);

    try {
      const response = await api.fetchConsentId(applicationId);
      if (response.consentId) {
        const finaliseState = window.btoa(`source=OpenBanking&applicationId=${applicationId}&productRedirectUrl=${productRedirectUrl}&consumerId=${consumerId}&bankName=${bankName}`).toString();
        setSpanStatus(loadInOpenBankingLoadingViewSpan, { code: SpanStatusCode.OK });
        endSpan(loadInOpenBankingLoadingViewSpan);
        redirectTo(`/onboard/finalise?state=${finaliseState}&id=${response.consentId}`);
      } else {
        pollTimerRef.current = setTimeout(() => pollBfosForConsentId(), POLLING_INTERVAL); //polling every 2 sec
      }
    } catch (error) {
      pollTimerRef.current = setTimeout(() => pollBfosForConsentId(), POLLING_INTERVAL); //retry on error
    }
  }, [api, applicationId, consumerId, loadInOpenBankingLoadingViewSpan, productRedirectUrl, state]);

  useEffect(() => {
    isPollingRef.current = true;
    const timeout = setTimeout(() => {
      isPollingRef.current = false;
      setSpanStatus(loadInOpenBankingLoadingViewSpan, { code: SpanStatusCode.ERROR });
      endSpan(loadInOpenBankingLoadingViewSpan);
      safeTrack(
        'LandOnBankFeedPortalErrorPage', {
          page: '/onboard/openbanking/connecting',
          businessId: consumerId,
          source: 'OpenBanking',
          consumer: 'SMEP',
          applicationId: applicationId,
          productFamily: 'SME',
          label: 'bank-feeds-portal',
          url: window.location.href
        }
      );
      setError(true)
    }, POLLING_TIMEOUT);

    messageTimerRef.current = setInterval(() => {
      setLoadingMessage((prev) => {
        const nextIndex = (MESSAGES.indexOf(prev) + 1) % MESSAGES.length;
        return MESSAGES[nextIndex];
      });
    }, 20000);

    pollBfosForConsentId();

    return () => {
      clearTimeout(timeout);
      if (messageTimerRef.current) {
        clearInterval(messageTimerRef.current);
      }
      if (pollTimerRef.current) {
        clearTimeout(pollTimerRef.current);
      }
      isPollingRef.current = false;
    };
  },[]);

  return (
    isError ? <ErrorViewV2 type='openBankingErrorCallRetryable' redirectUrl={productRedirectUrl}/> :
    <Loading message={loadingMessage}/>
  );
}

export default OpenBankingLoadingView;

OpenBankingLoadingView.propTypes = {
  productRedirectUrl: PropTypes.string,
  state: PropTypes.string
};
